import type { BackgroundIntensity } from '@/components/Loading/Overlay'

interface AppLoadingParams {
  isABApplied: boolean
  isLoadingUser: boolean
  isIdentified: boolean
  isLoggedIn: boolean
  hasBranding: boolean
  isLoadingBrandingTag: boolean
  eligibleBrandingTag: string | null
  isBrandingLoading: boolean
}

interface AppLoadingResult {
  isLoading: boolean
  backgroundIntensity: BackgroundIntensity
}

export function computeAppLoadingState(params: AppLoadingParams): AppLoadingResult {
  const {
    isABApplied,
    isLoadingUser,
    isIdentified,
    isLoggedIn,
    hasBranding,
    isLoadingBrandingTag,
    eligibleBrandingTag,
    isBrandingLoading,
  } = params

  // AB decisions require a user ID — skip the wait for anonymous users.
  // If the user has eligible branding, branding takes priority — don't block on AB.
  const waitingForAB = isLoggedIn && !isABApplied && !eligibleBrandingTag
  const waitingForUser = isLoadingUser || !isIdentified
  const waitingForOrders = isLoggedIn && hasBranding && isLoadingBrandingTag
  const waitingForBranding = !!eligibleBrandingTag && isBrandingLoading

  const isLoading = waitingForAB || waitingForUser || waitingForOrders || waitingForBranding

  // While a logged-in user's branded outcome is still unknown (identity/orders
  // pending on a branding app), the overlay must be opaque — a translucent
  // background leaks the unbranded UI and makes the branding swap visible.
  const brandedOutcomePending = isLoggedIn && hasBranding && (waitingForUser || waitingForOrders)
  const backgroundIntensity: BackgroundIntensity =
    (waitingForBranding || waitingForAB || brandedOutcomePending) ? 'solid' : 'medium'

  return { isLoading, backgroundIntensity }
}
